<template>
  <div class="container-fluid pm-page">
    <div class="pm-page-header pb-3">
      <h5 class="mb-0 font-weight-bold">{{ pageTitle }}</h5>
      <p class="text-secondary text-sm mb-0">{{ resolvedSubtitle }}</p>
    </div>

    <div class="row pb-2 pm-page-main">
      <div class="col-12 pm-page-col">
        <div class="card pm-page-card">
          <div class="card-header pb-0">
            <div class="tab-shell" role="tablist" :aria-label="`${pageTitle} tabs`">
              <button
                v-for="tab in displayTabs"
                :key="tab.key"
                type="button"
                class="tab-item"
                :class="{ active: activeTab === tab.key }"
                role="tab"
                :aria-selected="activeTab === tab.key"
                @click="activeTab = tab.key"
              >
                {{ tab.label }}
              </button>
            </div>
          </div>
          <div class="card-body px-0 pt-0 pb-2 pm-page-tab-body">
            <div v-if="isBoardLayout && activeTab === 'calendar'" class="pm-calendar-pane">
              <div class="pm-calendar-toolbar">
                <div class="pm-calendar-toolbar__cluster">
                  <div
                    v-if="showCalendarTeamToggle"
                    class="view-toggle view-toggle--team"
                    role="tablist"
                    aria-label="Calendar team"
                  >
                    <button
                      v-for="opt in calendarTeamToggleOptions"
                      :key="opt.key"
                      type="button"
                      class="view-toggle__btn"
                      :class="{ 'view-toggle__btn--active': calendarTeamFilter === opt.key }"
                      @click="calendarTeamFilter = opt.key"
                    >
                      {{ opt.label }}
                    </button>
                  </div>
                  <div
                    v-if="showCalendarGroupToggle"
                    class="view-toggle view-toggle--group"
                    role="tablist"
                    aria-label="Calendar group"
                  >
                    <button
                      type="button"
                      class="view-toggle__btn"
                      :class="{ 'view-toggle__btn--active': calendarGroupMode === 'team' }"
                      @click="calendarGroupMode = 'team'"
                    >
                      Team
                    </button>
                    <button
                      type="button"
                      class="view-toggle__btn"
                      :class="{ 'view-toggle__btn--active': calendarGroupMode === 'assign' }"
                      @click="calendarGroupMode = 'assign'"
                    >
                      Assign
                    </button>
                  </div>
                  <div
                    class="view-toggle view-toggle--entity"
                    role="tablist"
                    aria-label="Calendar item"
                  >
                    <button
                      type="button"
                      class="view-toggle__btn"
                      :class="{ 'view-toggle__btn--active': calendarEntityMode === 'subproject' }"
                      @click="calendarEntityMode = 'subproject'"
                    >
                      Sub project
                    </button>
                    <button
                      type="button"
                      class="view-toggle__btn"
                      :class="{ 'view-toggle__btn--active': calendarEntityMode === 'task' }"
                      @click="calendarEntityMode = 'task'"
                    >
                      Task
                    </button>
                  </div>
                  <div
                    class="view-toggle view-toggle--range"
                    role="tablist"
                    aria-label="Calendar range"
                  >
                    <button
                      type="button"
                      class="view-toggle__btn"
                      :class="{ 'view-toggle__btn--active': calendarRangeMode === 'sprint' }"
                      @click="calendarRangeMode = 'sprint'"
                    >
                      Sprint
                    </button>
                    <button
                      type="button"
                      class="view-toggle__btn"
                      :class="{ 'view-toggle__btn--active': calendarRangeMode === 'month' }"
                      @click="calendarRangeMode = 'month'"
                    >
                      Month
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="calendarLoading && !calendarPanels.length" class="text-secondary text-sm py-5 text-center">
                <i class="fas fa-spinner fa-spin me-1"></i>Loading calendar...
              </div>
              <div v-else-if="calendarError" class="alert alert-danger text-sm mx-4 mt-3" role="alert">
                {{ calendarError }}
              </div>
              <div v-else class="pm-calendar-stack">
                <team-sprint-detail-panel
                  v-for="panel in calendarPanels"
                  :key="panel.panelKey || panel.team"
                  :team="panel.team"
                  :heading="panel.heading || ''"
                  :loading="calendarLoading"
                  :error="panel.error || null"
                  :start-date="panel.startDate"
                  :end-date="panel.endDate"
                  :week-count="panel.weekCount"
                  :range-mode="calendarRangeMode"
                  :tasks="calendarTasksForPanel(panel)"
                  :milestones="panel.milestones"
                  :overdue-tasks="panel.overdueTasks"
                  :label-suffix="calendarLabelSuffix"
                />
              </div>
            </div>
            <test-plan-table
              v-else
              :team-filter="teamFilterForTable"
              :sprints="sprints"
              :history-mode="isHistoryTab"
              :board-mode="boardModeForTable"
              :lock-team-filter="isBoardLayout"
              :detail-tab="detailTab"
              :detail-from="resolvedDetailFrom"
              :detail-team="detailTeamCode"
              :detail-board="detailBoardCode"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import TestPlanTable from "./components/TestPlanTable";
import TeamSprintDetailPanel from "./components/TeamSprintDetailPanel.vue";
import { dashboardApi, sprintSettingsApi } from "@/services/backendApi";
import {
  defaultPlanTeamTabKey,
  visiblePlanTeamTabs,
} from "@/utils/userTeams";
import "@/assets/css/pm-tab-scroll.css";

const OVERVIEW_BOARD_TABS = [
  { key: "calendar", label: "Calendar" },
  { key: "ready", label: "Ready to Deploy" },
];

const TEAM_BOARD_TABS = [
  { key: "calendar", label: "Calendar" },
  { key: "current", label: "Current" },
  { key: "backlog", label: "Backlog" },
  { key: "history", label: "History" },
];

const EMPTY_CALENDAR = {
  startDate: null,
  endDate: null,
  weekCount: null,
  tasks: [],
  milestones: [],
  overdueTasks: [],
};

const TEAM_SCOPE_LABELS = {
  all: "Overview",
  "team a": "Team A",
  "team b": "Team B",
  unassign: "Team C",
};

export default {
  name: "team-plan-management",
  components: {
    TestPlanTable,
    TeamSprintDetailPanel,
  },
  props: {
    pageTitle: { type: String, default: "Plan Management" },
    pageSubtitle: { type: String, default: "Test Plan & Task tracking" },
    detailTab: { type: String, default: "" },
    detailFrom: { type: String, default: "team" },
    teamScope: { type: String, default: "" },
  },
  data() {
    return {
      activeTeam: "team a",
      activeBoardTab: "calendar",
      sprints: [],
      calendarLoading: false,
      calendarError: "",
      calendarRangeMode: "sprint",
      calendarEntityMode: "task",
      calendarGroupMode: "team",
      calendarTeamFilter: "all",
      calendarByTeam: {
        a: { team: "a", ...EMPTY_CALENDAR },
        b: { team: "b", ...EMPTY_CALENDAR },
        c: { team: "c", ...EMPTY_CALENDAR },
      },
    };
  },
  computed: {
    ...mapState(["user"]),
    isBoardLayout() {
      return !!this.teamScope || this.$route.matched.some((record) => (
        record.name === "Plan Management" || record.name === "Plan Management Team"
      ));
    },
    userTeamSource() {
      return this.user?.teams?.length ? this.user.teams : this.user?.team;
    },
    visibleTeamTabs() {
      return visiblePlanTeamTabs(this.userTeamSource);
    },
    displayTabs() {
      if (!this.isBoardLayout) return this.visibleTeamTabs;
      return this.isAllTeamScope ? OVERVIEW_BOARD_TABS : TEAM_BOARD_TABS;
    },
    allowedBoardTabKeys() {
      return this.displayTabs.map((tab) => tab.key);
    },
    activeTab: {
      get() {
        return this.isBoardLayout ? this.activeBoardTab : this.activeTeam;
      },
      set(value) {
        if (this.isBoardLayout) this.activeBoardTab = value;
        else this.activeTeam = value;
      },
    },
    resolvedSubtitle() {
      if (!this.isBoardLayout) return this.pageSubtitle;
      const teamLabel = TEAM_SCOPE_LABELS[this.teamScope] || "Overview";
      return `${this.pageSubtitle} — ${teamLabel}`;
    },
    teamFilterForTable() {
      if (this.isBoardLayout) return this.teamScope || "all";
      return this.activeTeam;
    },
    isHistoryTab() {
      return this.isBoardLayout ? this.activeBoardTab === "history" : this.activeTeam === "history";
    },
    boardModeForTable() {
      if (!this.isBoardLayout) return this.activeTeam === "history" ? "history" : "";
      if (this.activeBoardTab === "calendar") return "current";
      return this.activeBoardTab;
    },
    resolvedDetailFrom() {
      if (this.detailFrom && this.detailFrom !== "team") return this.detailFrom;
      const isPlanHub = this.$route.matched.some((record) => record.name === "Plan Management");
      return isPlanHub ? "plan" : "team";
    },
    detailTeamCode() {
      if (this.teamScope === "team a") return "a";
      if (this.teamScope === "team b") return "b";
      if (this.teamScope === "unassign") return "c";
      if (this.teamScope === "all") return "all";
      return "";
    },
    detailBoardCode() {
      return this.isBoardLayout ? this.activeBoardTab : "";
    },
    isAllTeamScope() {
      return this.teamScope === "all" || this.teamScope === "";
    },
    showCalendarTeamToggle() {
      return this.isAllTeamScope;
    },
    showCalendarGroupToggle() {
      return !this.isAllTeamScope;
    },
    calendarLabelSuffix() {
      if (this.calendarEntityMode === "subproject") return "";
      return this.isAllTeamScope ? "subproject" : "owner";
    },
    calendarTeamToggleOptions() {
      return [
        { key: "all", label: "All" },
        { key: "a", label: "A" },
        { key: "b", label: "B" },
        { key: "c", label: "C" },
      ];
    },
    calendarLoadCodes() {
      if (this.teamScope === "team a") return ["a"];
      if (this.teamScope === "team b") return ["b"];
      if (this.teamScope === "unassign") return ["c"];
      return ["a", "b", "c"];
    },
    calendarTeamCodes() {
      if (this.teamScope === "team a") return ["a"];
      if (this.teamScope === "team b") return ["b"];
      if (this.teamScope === "unassign") return ["c"];
      if (this.calendarTeamFilter === "a" || this.calendarTeamFilter === "b" || this.calendarTeamFilter === "c") {
        return [this.calendarTeamFilter];
      }
      return ["a", "b", "c"];
    },
    calendarPanels() {
      const source = this.calendarTeamCodes.map((code) => this.calendarByTeam[code]);
      if (this.isAllTeamScope || this.calendarGroupMode !== "assign") return source;
      return this.groupCalendarByAssignee(source);
    },
  },
  watch: {
    user: {
      handler() {
        this.initActiveTeamFromUser();
      },
      deep: true,
    },
    teamScope() {
      if (this.isAllTeamScope) this.calendarGroupMode = "team";
      this.ensureAllowedBoardTab();
      if (this.activeBoardTab === "calendar") this.loadCalendars();
    },
    activeBoardTab(tab) {
      if (tab === "calendar") this.loadCalendars();
    },
    "$route.query.board": {
      immediate: true,
      handler(board) {
        this.applyBoardFromQuery(board);
      },
    },
  },
  async created() {
    this.initActiveTeamFromUser();
    this.applyBoardFromQuery(this.$route.query?.board);
    await this.loadSprints();
    if (this.isBoardLayout) await this.loadCalendars();
  },
  mounted() {
    if (!this.isBoardLayout) {
      document.documentElement.classList.add("pm-scroll-lock");
    }
  },
  beforeUnmount() {
    const stayInHub = this.$route.matched.some((record) => (
      record.name === "QA Management"
      || record.name === "Plan Management"
      || record.name === "Plan Management Team"
    ));
    if (!stayInHub) {
      document.documentElement.classList.remove("pm-scroll-lock");
    }
  },
  methods: {
    ensureAllowedBoardTab() {
      if (!this.isBoardLayout) return;
      const allowed = this.allowedBoardTabKeys;
      if (!allowed.includes(this.activeBoardTab)) {
        this.activeBoardTab = allowed[0] || "calendar";
      }
    },
    applyBoardFromQuery(board) {
      if (!this.isBoardLayout) return;
      const key = String(board || "").toLowerCase();
      if (this.allowedBoardTabKeys.includes(key)) {
        this.activeBoardTab = key;
        return;
      }
      this.ensureAllowedBoardTab();
    },
    initActiveTeamFromUser() {
      if (this.isBoardLayout) return;
      const allowed = this.visibleTeamTabs.map((t) => t.key);
      if (!allowed.includes(this.activeTeam)) {
        this.activeTeam = defaultPlanTeamTabKey(this.userTeamSource);
      }
    },
    groupCalendarByAssignee(sourcePanels) {
      const byUser = new Map();
      const unassigned = [];
      const today = new Date().toISOString().slice(0, 10);
      const withDates = sourcePanels.filter((panel) => panel?.startDate && panel?.endDate);
      const currentSprint = withDates.find((panel) => panel.startDate <= today && today <= panel.endDate)
        || withDates[0]
        || sourcePanels[0]
        || {};
      const dates = {
        startDate: currentSprint.startDate || null,
        endDate: currentSprint.endDate || null,
        weekCount: currentSprint.weekCount != null ? currentSprint.weekCount : null,
      };
      const overlapsSprint = (task) => {
        if (!dates.startDate || !dates.endDate) return true;
        const start = task.startDate || task.endDate;
        const end = task.endDate || task.startDate;
        if (!start) return false;
        return start <= dates.endDate && end >= dates.startDate;
      };

      sourcePanels.forEach((panel) => {
        const team = panel?.team || "a";
        (Array.isArray(panel?.tasks) ? panel.tasks : []).forEach((task) => {
          if (!overlapsSprint(task)) return;
          const owners = Array.isArray(task.owners) ? task.owners : [];
          if (!owners.length) {
            unassigned.push({ ...task, sourceTeam: team });
            return;
          }
          owners.forEach((owner) => {
            const id = Number(owner.userId);
            if (!Number.isFinite(id)) return;
            if (!byUser.has(id)) {
              byUser.set(id, {
                userId: id,
                heading: owner.displayName || `User ${id}`,
                team,
                tasks: [],
              });
            }
            byUser.get(id).tasks.push({ ...task, sourceTeam: team });
          });
        });
      });

      const assigneeLabel = (task) => {
        const owners = Array.isArray(task.owners) ? task.owners : [];
        const names = owners.map((o) => o.displayName).filter(Boolean);
        return names.length ? names.join(", ") : "Unassigned";
      };
      const overdueOf = (tasks) => (Array.isArray(tasks) ? tasks : [])
        .filter((task) => task.endDate && !["done", "deployed", "cancel"].includes(String(task.status || "")))
        .filter((task) => String(task.endDate) < today)
        .sort((a, b) => String(a.endDate).localeCompare(String(b.endDate)))
        .map((task) => ({
          key: `task-${task.taskId}`,
          taskId: task.taskId,
          title: task.title,
          assignee: assigneeLabel(task),
          dueDate: task.endDate,
          status: task.status,
        }));

      const colorFor = (userId) => {
        const n = Math.abs(Number(userId) || 0);
        return ["a", "b", "c"][n % 3];
      };

      const panels = [...byUser.values()]
        .sort((a, b) => String(a.heading).localeCompare(String(b.heading), undefined, { sensitivity: "base" }))
        .map((group) => ({
          panelKey: `assign-${group.userId}`,
          team: colorFor(group.userId),
          heading: group.heading,
          error: "",
          startDate: dates.startDate,
          endDate: dates.endDate,
          weekCount: dates.weekCount,
          tasks: group.tasks,
          milestones: [],
          overdueTasks: overdueOf(group.tasks),
        }));

      if (unassigned.length) {
        panels.push({
          panelKey: "assign-unassigned",
          team: "c",
          heading: "Unassigned",
          error: "",
          startDate: dates.startDate,
          endDate: dates.endDate,
          weekCount: dates.weekCount,
          tasks: unassigned,
          milestones: [],
          overdueTasks: overdueOf(unassigned),
        });
      }
      if (panels.length) return panels;
      return [{
        panelKey: "assign-empty",
        team: "a",
        heading: "Unassigned",
        error: "",
        startDate: dates.startDate,
        endDate: dates.endDate,
        weekCount: dates.weekCount,
        tasks: [],
        milestones: [],
        overdueTasks: [],
      }];
    },
    calendarTasksForPanel(panel) {
      const tasks = (Array.isArray(panel?.tasks) ? panel.tasks : []).filter((task) => (
        !task?.isSubTask && task?.ownerTaskId == null
      ));
      if (this.calendarEntityMode === "subproject") {
        return this.groupTasksBySubProject(tasks);
      }
      return tasks.filter((task) => !this.isSubProjectCalendarRow(task));
    },
    isSubProjectCalendarRow(task) {
      if (task?.isPlanRoot) return true;
      const title = String(task?.title || "").trim().toLowerCase();
      const subName = String(task?.subProjectName || "").trim().toLowerCase();
      return Boolean(title && subName && title === subName);
    },
    groupTasksBySubProject(tasks) {
      const groups = new Map();
      tasks.forEach((task) => {
        const id = task.subProjectId != null ? Number(task.subProjectId) : 0;
        const title = task.subProjectName || "Uncategorized";
        if (!groups.has(id)) {
          groups.set(id, {
            taskId: id || null,
            title,
            status: task.status,
            startDate: task.startDate || null,
            endDate: task.endDate || task.startDate || null,
          });
          return;
        }
        const group = groups.get(id);
        if (task.startDate && (!group.startDate || task.startDate < group.startDate)) {
          group.startDate = task.startDate;
        }
        if (task.endDate && (!group.endDate || task.endDate > group.endDate)) {
          group.endDate = task.endDate;
        }
        group.status = this.mergeCalendarStatus(group.status, task.status);
      });
      return [...groups.values()];
    },
    mergeCalendarStatus(current, next) {
      const rank = (status) => {
        if (status === "bug" || status === "blocked") return 4;
        if (status === "in_progress" || status === "testing") return 3;
        if (status === "to_do") return 2;
        if (status === "done" || status === "deployed" || status === "done_without_deploy") return 1;
        return 2;
      };
      return rank(next) > rank(current) ? next : current;
    },
    async loadSprints() {
      try {
        this.sprints = await sprintSettingsApi.getAll();
      } catch (e) {
        console.error("loadSprints error:", e);
        this.sprints = [];
      }
    },
    async loadCalendars() {
      this.calendarLoading = true;
      this.calendarError = "";
      try {
        const results = await Promise.all(
          this.calendarLoadCodes.map(async (team) => {
            const data = await dashboardApi.getTeamSprintCalendar(team);
            return { team, data };
          }),
        );
        const next = { ...this.calendarByTeam };
        results.forEach(({ team, data }) => {
          next[team] = {
            team,
            error: "",
            startDate: data?.startDate || null,
            endDate: data?.endDate || null,
            weekCount: data?.weekCount != null ? Number(data.weekCount) : null,
            tasks: Array.isArray(data?.tasks) ? data.tasks : [],
            milestones: Array.isArray(data?.milestones) ? data.milestones : [],
            overdueTasks: Array.isArray(data?.overdueTasks) ? data.overdueTasks : [],
          };
        });
        this.calendarByTeam = next;
      } catch (e) {
        console.error("loadCalendars error:", e);
        this.calendarError = e.message || "Failed to load calendar.";
      } finally {
        this.calendarLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.pm-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

.pm-page-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pm-page-col {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.pm-page-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pm-page-tab-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pm-calendar-pane {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0.75rem 1.25rem 1.25rem;
}

.pm-calendar-toolbar {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 0.85rem;
  background: transparent;
}

.pm-calendar-toolbar__cluster {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
}

.pm-calendar-stack {
  flex: 1;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.pm-calendar-stack > * {
  flex: 0 0 auto;
}

.card-header.pb-0 {
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
}

.view-toggle {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 0.25rem;
  padding: 0.25rem;
  border-radius: 10px;
  background: #eef1f6;
}

.view-toggle__btn {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  border-radius: 8px;
  padding: 0.4rem 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #67748e;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
}

.view-toggle--team .view-toggle__btn {
  width: 2.5rem;
}

.view-toggle--range .view-toggle__btn {
  width: 4.35rem;
}

.view-toggle--entity .view-toggle__btn {
  width: 7.1rem;
}
.view-toggle--group .view-toggle__btn {
  width: 4.6rem;
}

.view-toggle__btn--active {
  background: #fff;
  color: #344767;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.tab-shell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.3rem;
  border-radius: 12px;
  background: transparent;
}

.tab-item {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #696969;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.55rem 0.9rem;
  line-height: 1.2;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-item.active {
  color: #ffffff;
  background: #344767;
  box-shadow: 0 6px 14px rgba(52, 71, 103, 0.28);
}
</style>
